/**
 * Family members and education history — a person's own first-time personal-details entry,
 * usually filled in during onboarding's "Complete personal details" task (see
 * apps/web/src/features/employees/MyDetailsPage.tsx's Family/Education tabs and
 * apps/web/src/features/onboarding/MyOnboardingPage.tsx's deep link into them).
 *
 * Deliberately NOT routed through the `ProfileUpdateRequest` approval flow (see that model's own
 * schema comment): this is a new hire stating their own history for the first time, not a
 * correction to an official record HR already holds — self-service, immediate, no reviewer.
 *
 * Both resources are scoped by `personId` (not hardcoded to "the caller"), so the route can serve
 * an employee viewing/editing their own records OR an HR admin looking one up from the Employee
 * Directory later — the route layer decides which via `can()`, this contract just describes the
 * shape.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { dateOnlySchema, errorBodySchema, orgIdSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const familyRelationshipSchema = z.enum([
  'spouse',
  'father',
  'mother',
  'child',
  'sibling',
  'other',
]);

export const educationLevelSchema = z.enum([
  'secondary',
  'senior_secondary',
  'diploma',
  'undergraduate',
  'postgraduate',
  'doctorate',
  'certification',
]);

export const socialPlatformSchema = z.enum([
  'linkedin',
  'github',
  'twitter',
  'facebook',
  'instagram',
  'website',
  'other',
]);

export const socialLinkSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  platform: socialPlatformSchema,
  url: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SocialLink = z.infer<typeof socialLinkSchema>;

export const createSocialLinkBodySchema = z.object({
  platform: socialPlatformSchema,
  // https only: this URL is rendered as a clickable link, so `javascript:` and friends are out.
  url: z
    .string()
    .trim()
    .max(500)
    .url()
    .refine((value) => value.startsWith('https://'), 'Link must start with https://'),
});

/**
 * What a family or education change returns when it needs HR's approval instead of being applied:
 * nothing was saved yet, and this is the pending request that will apply it if approved.
 */
export const approvalPendingSchema = z.object({
  status: z.literal('pending_approval'),
  requestId: uuidSchema,
});
export type ApprovalPending = z.infer<typeof approvalPendingSchema>;

export const familyMemberSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  fullName: z.string(),
  relationship: familyRelationshipSchema,
  dateOfBirth: dateOnlySchema.nullable(),
  occupation: z.string().nullable(),
  phone: z.string().nullable(),
  isDependent: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FamilyMember = z.infer<typeof familyMemberSchema>;

export const educationSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  level: educationLevelSchema,
  institution: z.string(),
  fieldOfStudy: z.string().nullable(),
  startYear: z.number().int().nullable(),
  endYear: z.number().int().nullable(),
  grade: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Education = z.infer<typeof educationSchema>;

export const upsertFamilyMemberBodySchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  relationship: familyRelationshipSchema,
  dateOfBirth: dateOnlySchema.nullable().optional(),
  occupation: z.string().trim().max(200).nullable().optional(),
  phone: z.string().trim().max(30).nullable().optional(),
  isDependent: z.boolean().default(false),
});

export const upsertEducationBodySchema = z.object({
  level: educationLevelSchema,
  institution: z.string().trim().min(1).max(300),
  fieldOfStudy: z.string().trim().max(200).nullable().optional(),
  startYear: z.number().int().min(1950).max(2100).nullable().optional(),
  endYear: z.number().int().min(1950).max(2100).nullable().optional(),
  grade: z.string().trim().max(100).nullable().optional(),
});

export const personalDetailsContract = c.router({
  listFamilyMembers: {
    method: 'GET',
    path: '/people/:personId/family-members',
    responses: { 200: z.array(familyMemberSchema), 401: errorBodySchema, 403: errorBodySchema },
    summary: "A person's self-reported family members/dependents",
    metadata: routeMeta(null),
  },
  createFamilyMember: {
    method: 'POST',
    path: '/people/:personId/family-members',
    body: upsertFamilyMemberBodySchema,
    responses: {
      201: familyMemberSchema,
      202: approvalPendingSchema,
      401: errorBodySchema,
      403: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  updateFamilyMember: {
    method: 'PATCH',
    path: '/people/:personId/family-members/:familyMemberId',
    body: upsertFamilyMemberBodySchema.partial(),
    responses: {
      200: familyMemberSchema,
      202: approvalPendingSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  deleteFamilyMember: {
    method: 'DELETE',
    path: '/people/:personId/family-members/:familyMemberId',
    body: c.noBody(),
    responses: {
      204: c.noBody(),
      202: approvalPendingSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  listEducations: {
    method: 'GET',
    path: '/people/:personId/educations',
    responses: { 200: z.array(educationSchema), 401: errorBodySchema, 403: errorBodySchema },
    summary: "A person's self-reported education history",
    metadata: routeMeta(null),
  },
  createEducation: {
    method: 'POST',
    path: '/people/:personId/educations',
    body: upsertEducationBodySchema,
    responses: {
      201: educationSchema,
      202: approvalPendingSchema,
      401: errorBodySchema,
      403: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  updateEducation: {
    method: 'PATCH',
    path: '/people/:personId/educations/:educationId',
    body: upsertEducationBodySchema.partial(),
    responses: {
      200: educationSchema,
      202: approvalPendingSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  deleteEducation: {
    method: 'DELETE',
    path: '/people/:personId/educations/:educationId',
    body: c.noBody(),
    responses: {
      204: c.noBody(),
      202: approvalPendingSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  listSocialLinks: {
    method: 'GET',
    path: '/people/:personId/social-links',
    responses: { 200: z.array(socialLinkSchema), 401: errorBodySchema, 403: errorBodySchema },
    summary: "A person's social / profile links",
    metadata: routeMeta(null),
  },
  createSocialLink: {
    method: 'POST',
    path: '/people/:personId/social-links',
    body: createSocialLinkBodySchema,
    responses: { 201: socialLinkSchema, 401: errorBodySchema, 403: errorBodySchema },
    metadata: routeMeta(null),
  },
  deleteSocialLink: {
    method: 'DELETE',
    path: '/people/:personId/social-links/:socialLinkId',
    body: c.noBody(),
    responses: {
      204: c.noBody(),
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
});
